import React from 'react';
import { FaCheck } from 'react-icons/fa';

/**
 * Componente de checkbox personalizado con diseño moderno
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.checked - Estado del checkbox (marcado/desmarcado)
 * @param {function} props.onChange - Función que se ejecuta al cambiar el estado
 * @param {string} props.label - Texto del label
 * @param {string} props.id - ID único para el checkbox (opcional)
 * @param {string} props.name - Nombre del input (opcional)
 * @param {string} props.className - Clases CSS adicionales (opcional)
 * @param {boolean} props.disabled - Si el checkbox está deshabilitado (opcional)
 * @param {string} props.size - Tamaño del checkbox: 'sm', 'md', 'lg' (opcional, default: 'md')
 * @param {string} props.labelClassName - Clases CSS adicionales para el label (opcional)
 */
const CustomCheckbox = ({
  checked = false,
  onChange,
  label,
  id,
  name,
  className = '',
  disabled = false,
  size = 'md',
  labelClassName = ''
}) => {
  // Configuración de tamaños
  const sizeConfig = {
    sm: {
      box: 'w-4 h-4',
      icon: 'text-[8px]',
      label: 'text-xs',
      spacing: 'space-x-2'
    },
    md: {
      box: 'w-5 h-5',
      icon: 'text-[10px]',
      label: 'text-sm',
      spacing: 'space-x-2'
    },
    lg: {
      box: 'w-6 h-6',
      icon: 'text-xs',
      label: 'text-base',
      spacing: 'space-x-2'
    }
  };

  const config = sizeConfig[size] || sizeConfig.md;

  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const handleKeyPress = (e) => {
    if ((e.key === ' ' || e.key === 'Enter') && !disabled) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div 
      className={`flex items-center ${config.spacing} ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      } ${className}`}
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      tabIndex={disabled ? -1 : 0}
      role="checkbox"
      aria-checked={checked}
      aria-labelledby={id ? `${id}-label` : undefined}
    >
      {/* Hidden input para compatibilidad con formularios */}
      <input
        type="checkbox"
        id={id}
        name={name}
        checked={checked}
        onChange={() => {}} // Manejado por el div padre
        className="sr-only"
        disabled={disabled}
        tabIndex={-1}
      />
      
      {/* Checkbox visual personalizado */}
      <div className={`
        ${config.box}
        flex-shrink-0 
        rounded 
        border-2 
        flex 
        items-center 
        justify-center 
        transition-all 
        duration-200
        ${checked 
          ? 'bg-blue-500 border-blue-500' 
          : disabled
            ? 'bg-gray-700 border-gray-600'
            : 'bg-gray-600 border-gray-500 hover:border-gray-400'
        }
      `}>
        {checked && (
          <FaCheck 
            className={`text-white ${config.icon}`} 
            aria-hidden="true"
          />
        )}
      </div>
      
      {/* Label */}
      {label && (
        <label 
          id={id ? `${id}-label` : undefined}
          className={`
            ${config.label}
            cursor-pointer 
            select-none
            pr-1
            ${disabled ? 'text-gray-500' : 'text-gray-300'}
            ${labelClassName}
          `}
        >
          {label}
        </label>
      )}
    </div>
  );
};

export default CustomCheckbox;
